import React from 'react';
import { CheckCircle2, MapPin, Award, Users } from 'lucide-react';

const AboutSection = () => {
  const highlights = [
    'New "one-trip" 20ft shipping containers',
    'Fully concreted yard - accessible by car, van or lorry',
    'Easy opening handles for convenient access',
    'Heavy-duty padlock and lock box included',
    'Secure, well-maintained facility',
    'Flexible rental periods with great discounts',
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-full">
              <div className="space-y-4 w-full">
                <div className="rounded-xl overflow-hidden shadow-lg w-full">
                  <img
                    src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/91hfpjmi_RSSS%20LOGO.1.jpeg"
                    alt="Red Sail Storage Facility"
                    className="w-full object-cover"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '192px', objectFit: 'cover', display: 'block' }}
                  />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg w-full">
                  <img
                    src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/aeyucykm_20241220_113513.jpg"
                    alt="Storage Containers"
                    className="w-full object-cover"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '256px', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              </div>
              <div className="md:pt-8 w-full">
                <div className="rounded-xl overflow-hidden shadow-lg w-full">
                  <img
                    src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/aeyucykm_20241220_113513.jpg"
                    alt="Secure Storage"
                    className="w-full object-cover"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '320px', objectFit: 'cover', display: 'block', maxWidth: '100%' }}
                  />
                </div>
              </div>
            </div>

            {/* Stats Card Overlay */}
            <div className="absolute -bottom-6 left-6 right-6 bg-red-600 rounded-xl shadow-xl p-6 text-white">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">5+</p>
                  <p className="text-xs text-red-100">Years Experience</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">100+</p>
                  <p className="text-xs text-red-100">Happy Customers</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">24/7</p>
                  <p className="text-xs text-red-100">Security</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-6 lg:pl-8">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                <Award className="h-4 w-4 mr-2" />
                About Us
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              Secure, Clean & Affordable Storage Solutions
            </h2>

            <p className="text-lg text-gray-600 leading-relaxed">
              At Red Sail Self Storage, we provide premium container storage solutions in St. Osyth, Essex. 
              Our facility features brand new "one-trip" 20ft shipping containers, each equipped with easy-access 
              handles and industry-standard security features.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed">
              Located at Blackwater Farm in the heart of Clacton-on-Sea, we offer a secure, well-maintained 
              facility with daily access and competitive pricing. Whether you need short-term or long-term storage, 
              we have flexible options to suit your needs.
            </p>

            {/* Highlights */}
            <div className="space-y-3">
              {highlights.map((highlight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{highlight}</span>
                </div>
              ))}
            </div>

            {/* Location Info */}
            <div className="flex items-start space-x-4 pt-4 p-6 bg-gray-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Our Location</h3>
                <a
                  href="https://www.google.com/maps/place/Red+Sail+Self+Storage/@51.7906198,1.0657016,14.75z/data=!4m6!3m5!1s0x47d91305174b3999:0xd049b71ff0ddf7b3!8m2!3d51.7919515!4d1.0558218!16s%2Fg%2F11rqldndt7?hl=en-GB&entry=ttu&g_ep=EgoyMDI2MDUyMC4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-red-600 transition-colors"
                >
                  Blackwater Farm, Lee Wick Lane<br />
                  St. Osyth, Clacton-on-Sea<br />
                  Essex, CO16 8ES
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
